import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toAbsoluteUrl } from '../../../_metronic/helpers';

/**
 * Página de onboarding para novos clientes Nuvemshop (v0 simplificada)
 * 
 * 1. Recebe code da URL
 * 2. Troca code por access_token permanente e salva no MongoDB
 * 3. Exibe Calendly para agendamento com vendas
 */
export function NuvemshopCalendlyPage() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const code = query.get('code');
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [storeName, setStoreName] = useState('');

  useEffect(() => {
    if (code) {
      saveToken();
    } else {
      setError(true);
      setErrorMessage('Código de autorização não encontrado');
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const saveToken = async () => {
    try {
      setIsLoading(true);
      setError(false);
      
      console.log('[CALENDLY] Salvando token permanente...');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/onboarding/nuvemshop/save-token`,
        { code }
      );
      
      if (response.data.success) {
        console.log('[CALENDLY] ✅ Token salvo com sucesso');
        setStoreName(response.data.storeName || '');
        setIsLoading(false);
        
        // Carregar Calendly widget
        loadCalendlyWidget();
      }
    } catch (err: any) {
      console.error('[CALENDLY] Erro ao salvar token:', err);
      setError(true);
      setErrorMessage(
        err.response?.data?.error || 
        'Não foi possível processar sua solicitação. Por favor, tente novamente.'
      );
      setIsLoading(false);
    }
  };

  const loadCalendlyWidget = () => {
    // Verificar se o script já foi carregado
    if (document.querySelector('script[src*="calendly.com"]')) {
      console.log('[CALENDLY] Script já carregado');
      return;
    }

    // Carregar script do Calendly
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    
    // Carregar CSS do Calendly
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    console.log('[CALENDLY] Script carregado');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className='d-flex flex-column flex-root'>
        <div className='d-flex flex-column flex-column-fluid text-center p-10'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Carregando...</span>
          </div>
          <p className='text-gray-600 fs-4 fw-bold mt-5'>Preparando seu atendimento...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='d-flex flex-column flex-root'>
        <div
          className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
          style={{
            backgroundImage: `url('${toAbsoluteUrl('/media/illustrations/sketchy-1/17.png')}')`,
          }}
        >
          <div className='d-flex flex-column flex-column-fluid text-center p-10 py-lg-20'>
            <div className='pt-lg-10'>
              <div className='card shadow-sm mx-auto' style={{ maxWidth: '500px' }}>
                <div className='card-body p-10 text-center'>
                  <div className='text-danger mb-5' style={{ fontSize: '4rem' }}>⚠️</div>
                  <h2 className='fw-bolder text-gray-900 mb-5 fs-2x'>
                    Ops! Algo deu errado
                  </h2>
                  <p className='text-gray-600 fs-5 mb-10'>
                    {errorMessage}
                  </p>
                  <div className='d-flex flex-column gap-3'>
                    <a
                      href='https://wa.me/551151980535?text=Olá,%20preciso%20de%20ajuda%20com%20a%20instalação%20da%20Konfidency+na+Nuvemshop'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='btn btn-success btn-lg fw-bold'
                    >
                      💬 Falar no WhatsApp
                    </a>
                    <button
                      onClick={() => window.location.reload()}
                      className='btn btn-light-primary btn-lg fw-bold'
                    >
                      🔄 Tentar novamente
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success - Show Calendly
  return (
    <div className='d-flex flex-column flex-root' style={{ minHeight: '100vh', backgroundColor: '#f5f8fa' }}>
      {/* Logo */}
      <div className='d-flex flex-center flex-column-auto p-10'>
        <a href='/' className='mb-5'>
          <img
            alt='Konfidency Logo'
            src={toAbsoluteUrl('/media/logos/logo-2-dark.svg')}
            className='h-40px'
          />
        </a>
      </div>

      {/* Content */}
      <div className='container py-5'>
        <div className='row justify-content-center'>
          <div className='col-xl-10 col-xxl-9'>
            {/* Header Card */}
            <div className='card shadow-sm mb-5'>
              <div className='card-body p-10 text-center'>
                <h1 className='fw-bolder text-gray-900 mb-5 fs-2x'>
                  👋 Olá{storeName && `, ${storeName}`}!
                </h1>
                <p className='text-gray-700 fs-4 mb-3'>
                  Ficamos felizes com seu interesse no <strong>Konfidency Reviews</strong>
                </p>
                <p className='text-gray-600 fs-5'>
                  Agende uma conversa rápida com nosso time para conhecer nossos planos
                  e tirar todas as suas dúvidas.
                </p>
              </div>
            </div>

            {/* Info Alert */}
            <div className='alert alert-primary d-flex align-items-center mb-5'>
              <span className='fs-2x me-4'>💡</span>
              <div className='flex-grow-1'>
                <h4 className='alert-heading mb-2'>O que acontece depois?</h4>
                <p className='mb-0'>
                  Após nossa conversa, vamos te ajudar com toda a configuração e instalação do sistema na sua loja!
                </p>
              </div>
            </div>

            {/* Calendly Widget */}
            <div className='card shadow-sm mb-5'>
              <div className='card-body p-5'>
                <div 
                  className='calendly-inline-widget' 
                  data-url='https://calendly.com/d/yv7-64m-58b/demonstracao-konfidency?hide_gdpr_banner=1&primary_color=dc239b'
                  style={{ 
                    minWidth: '320px', 
                    height: '700px',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                />
              </div>
            </div>

            {/* Footer CTA */}
            <div className='card shadow-sm bg-light-success'>
              <div className='card-body p-8 text-center'>
                <p className='text-gray-800 fs-5 mb-4'>
                  <strong>Prefere falar agora?</strong>
                </p>
                <a
                  href='https://wa.me/551151975175?text=Olá,%20vim%20da%20Nuvemshop%20e%20quero%20conhecer%20a%20Konfidency'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='btn btn-success btn-lg fw-bold'
                >
                  💬 Clique aqui para WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='d-flex flex-center flex-column-auto p-10'>
        <div className='d-flex align-items-center fw-bold fs-6'>
          <a href='https://www.konfidency.cx' className='text-muted text-hover-primary px-2'>
            Sobre a Konfidency
          </a>
          
        </div>
      </div>
    </div>
  );
}
